'use client'

import { memo, useEffect, useRef, useState } from 'react'
import { resolveBlockType } from '@/lib/notebook/registry/blocks-registry'
import SectionRenderer from './SectionRenderer'

const INTRINSIC_MIN_HEIGHT = {
  block_hero: 280,
  block_tabs: 52,
  block_richtext: 220,
  block_card: 200,
  block_zone: 240,
  block_code: 180,
  block_media: 200,
  block_timeline: 260,
}

function intrinsicMinHeight(type) {
  const resolved = resolveBlockType(type)
  return INTRINSIC_MIN_HEIGHT[resolved] ?? 160
}

/**
 * Defers heavy block editor mount only — BlockSection chrome (hover actions) stays intact.
 */
function DeferredSectionBodyInner({ section, structural = false, priority = false }) {
  const rootRef = useRef(null)
  const [mounted, setMounted] = useState(priority)

  useEffect(() => {
    if (priority || mounted) return undefined
    const el = rootRef.current
    if (!el) return undefined

    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true)
      return undefined
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        setMounted(true)
        io.disconnect()
      },
      { root: null, rootMargin: '480px 0px', threshold: 0 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [priority, mounted])

  if (mounted) {
    return <SectionRenderer section={section} structural={structural} />
  }

  const minH = intrinsicMinHeight(section.type)
  return (
    <div
      ref={rootRef}
      className="nb-block-deferred-placeholder"
      aria-hidden="true"
      style={{ minHeight: minH }}
    />
  )
}

function bodyPropsEqual(prev, next) {
  const a = prev.section
  const b = next.section
  return (
    a.id === b.id &&
    a.type === b.type &&
    a.data === b.data &&
    prev.structural === next.structural &&
    prev.priority === next.priority
  )
}

export const DeferredSectionBody = memo(DeferredSectionBodyInner, bodyPropsEqual)
